// 화면 껍데기. **플랫폼과 세션을 props 로 받는다** — 그래서 웹·데스크톱·모바일이
// 이 파일을 그대로 쓴다.
//
// `apps/web` 이 아니라 여기 있는 이유: 앱끼리는 서로를 import 하지 않는다.
// 공유하고 싶은 것이 생기면 앱에 두고 건너뛰는 게 아니라 패키지로 올린다.
//
// ## 세션은 네 함수짜리 인터페이스다
// `@maru/client` 를 임포트하지 않는다. 이 파일이 아는 것은 "시작·멈춤·구독·훑기" 뿐이라
// 시험은 가짜 세션으로 돌고, 이 패키지는 여전히 `fetch` 도 `WebSocket` 도 모른다.

import type { Platform } from '@maru/platform'
import { lazy, Suspense, useEffect, useState, useSyncExternalStore } from 'react'
import { ConnIndicator, type ConnState } from './conn_indicator.tsx'
import { msg, useT } from './i18n.ts'
import { type FaceSource, FaceSources } from './kit/display.tsx'
import { createToastStore, ToastContext, type ToastStore } from './kit/toast.tsx'

// 알림 판은 첫 알림이 뜰 때 받는다 — 아무 일도 안 한 첫 화면에는 필요 없다.
const Toaster = lazy(() => import('./kit/toaster.tsx').then((m) => ({ default: m.Toaster })))

export interface ScreenState {
  conn: ConnState
  badge: number
}

/** `@maru/client` 의 `Session` 과 같은 모양이다. 거기서 이걸 구현한다. */
export interface Session {
  start(): Promise<void>
  stop(): void
  subscribe(listen: () => void): () => void
  scan(): ScreenState
  /** 얼굴 사진 출처. 주면 안의 모든 `Avatar user=…` 가 사진을 스스로 찾는다. */
  faces?: FaceSource
}

/** 플랫폼 이름은 코드가 가르는 열쇠라 한국어로 고정돼 있다. 보일 때만 옮긴다. */
const SHELL_NAME: Record<Platform['name'], string> = {
  웹: msg('웹'),
  데스크톱: msg('데스크톱'),
  모바일: msg('모바일'),
}

export function Shell({
  platform,
  session,
  children,
  toasts,
}: {
  platform: Platform
  session: Session
  /** 완료·실패 알림 가게. 안 주면 셸이 하나 만든다. 본문은 `useToasts()` 로 쥔다. */
  toasts?: ToastStore
  /** 본문. 세션이 붙는 것과 무엇을 그릴지는 다른 관심사라 주입으로 받는다. */
  children?: React.ReactNode
}) {
  // 서버 렌더에서도 같은 훑기를 쓴다 — 상태가 외부에 있으니 스냅샷이 곧 진실이다.
  const t = useT()
  const status = useSyncExternalStore(session.subscribe, session.scan, session.scan)
  const [store] = useState(() => toasts ?? createToastStore())
  // 한 번 뜨면 판을 그대로 둔다 — 알림이 다 닫힐 때마다 내렸다 올리면 스크린리더가 판을 놓친다.
  const [ever, set_ever] = useState(false)
  const any = useSyncExternalStore(
    store.subscribe,
    () => store.list().length > 0,
    () => false,
  )
  if (any && !ever) set_ever(true)

  useEffect(() => {
    // 시작 실패(토큰 없음·서버 없음)는 던지지 않고 남긴다. 화면은 '끊김' 띠를 보이고,
    // 엔진이 스스로 다시 붙는다 — 여기서 잡지 않으면 React 가 트리를 통째로 버린다.
    session.start().catch((e: unknown) => console.error('session.start', e))
    return () => session.stop()
  }, [session])

  useEffect(() => {
    platform.badge(status.badge)
  }, [platform, status.badge])
  // 세션이 내려가면(로그아웃·조직 바꾸기) 배지도 내린다 — 로그인 화면 탭에 "(1) maru" 가 남으면
  // 앞 사람의 안 읽음이 다음 사람에게 보인다(3차 J21).
  useEffect(() => () => platform.badge(0), [platform])

  return (
    <div className="maru">
      <ConnIndicator status={status.conn} />
      <ToastContext value={store}>
        <FaceSources value={session.faces ?? null}>
          <main>
            {children ?? (
              <p>{t('{name}에서 실행 중이에요.', { name: t(SHELL_NAME[platform.name]) })}</p>
            )}
          </main>
        </FaceSources>
      </ToastContext>
      {(ever || any) && (
        <Suspense fallback={null}>
          <Toaster store={store} />
        </Suspense>
      )}
    </div>
  )
}
